import React, { useCallback, useEffect, useMemo, useState } from "react"
import styles from "./outboundCampaignList.module.css"
import Table from "../../components/Table"
import api from "../../services/api"
import { toast } from "react-toastify"
import Loading from "../layouts/Loading/index"
import moment from "moment"
import { getPropertyForItemInArrayById } from "../../utils/utils"
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom"
import BackButton from "../../components/BackButton"
import DatePicker from "../../components/Form/DatePicker"
import Select from "../../components/Form/Select"
import Input from "../../components/Form/Input"
import Button from "../../components/Form/Button"
import ModalConfirm from "../../components/Modal/ModalConfirm"
import Checkbox from "../../components/Form/Checkbox"
import PdfExportButton from "../Dashboard/PdfExportButton"
import { formatPeriod, getOptionLabels } from "../../utils/dashboardPdf"
import { useAuth } from "../../contexts/authContext"
import CampaignScheduleModal from "./CampaignScheduleModal"
import CampaignComparisonModal from "./CampaignComparisonModal"
import CampaignRetryModal from "./CampaignRetryModal"
import {
    MdAccessTime,
    MdAdd,
    MdDoneAll,
    MdErrorOutline,
    MdFilterAltOff,
    MdInbox,
    MdOutlineCampaign,
    MdOutlineRefresh,
    MdReply,
    MdSchedule,
    MdSend,
    MdCompareArrows,
    MdContentCopy,
    MdPause,
    MdPlayArrow,
    MdPersonOutline,
} from "react-icons/md"

const optionsStatus = [
    { id: "draft", value: "Rascunho" },
    { id: "scheduled", value: "Agendada" },
    { id: "sending", value: "Enviando" },
    { id: "finished", value: "Finalizada" },
    { id: "paused", value: "Pausada" },
    { id: "canceled", value: "Cancelada" },
]

const sortOptions = [
    { id: "recent", value: "Mais recentes" },
    { id: "oldest", value: "Mais antigas" },
    { id: "schedule_asc", value: "Agendamento mais próximo" },
    { id: "schedule_desc", value: "Agendamento mais distante" },
    { id: "name_asc", value: "Nome A–Z" },
    { id: "name_desc", value: "Nome Z–A" },
]

const statusTone = {
    draft: "neutral",
    scheduled: "info",
    sending: "warning",
    finished: "success",
    paused: "warning",
    canceled: "danger",
}

const summaryIcons = {
    total_campaigns: MdOutlineCampaign,
    sending_campaigns: MdSend,
    scheduled_campaigns: MdSchedule,
    messages_sent: MdSend,
    delivery_rate: MdDoneAll,
    reply_rate: MdReply,
    messages_failed: MdErrorOutline,
}

const getDefaultFilters = () => ({
    dateStart: moment().subtract(30, "d").format("YYYY-MM-DD"),
    dateEnd: moment().add(15, "d").format("YYYY-MM-DD"),
    status: [],
    inboxes: [],
    search: "",
    health: "",
    sort: "recent",
})

const getFiltersFromParams = params => {
    const defaults = getDefaultFilters()
    const allPeriod = params.get("period") === "all"
    return {
        dateStart: allPeriod ? "" : (params.get("dateStart") || defaults.dateStart),
        dateEnd: allPeriod ? "" : (params.get("dateEnd") || defaults.dateEnd),
        status: params.getAll("status"),
        inboxes: params.getAll("inbox_id"),
        search: params.get("search") || "",
        health: params.get("health") || "",
        sort: params.get("sort") || "recent",
    }
}

const getParamsFromFilters = filters => {
    const params = new URLSearchParams()
    if (filters.dateStart) params.set("dateStart", filters.dateStart)
    if (filters.dateEnd) params.set("dateEnd", filters.dateEnd)
    if (!filters.dateStart && !filters.dateEnd) params.set("period", "all")
    if (filters.search?.trim()) params.set("search", filters.search.trim())
    if (filters.health) params.set("health", filters.health)
    if (filters.sort && filters.sort !== "recent") params.set("sort", filters.sort)
    filters.status?.forEach(status => params.append("status", status))
    filters.inboxes?.forEach(inboxId => params.append("inbox_id", inboxId))
    return params
}

const OutboundCampaignList = () => {
    const [searchParams, setSearchParams] = useSearchParams()
    const initialFilters = useMemo(() => getFiltersFromParams(searchParams), [])
    const [campaigns, setCampaigns] = useState([])
    const [cards, setCards] = useState([])
    const [highlights, setHighlights] = useState([])
    const [inboxOptions, setInboxOptions] = useState([])
    const [max, setMax] = useState(0)
    const [page, setPage] = useState(1)
    const [totalPages, setTotalPages] = useState(1)
    const [filters, setFilters] = useState(initialFilters)
    const [appliedFilters, setAppliedFilters] = useState(initialFilters)
    const [loading, setLoading] = useState(false)
    const [loadingMore, setLoadingMore] = useState(false)
    const [refreshing, setRefreshing] = useState(false)
    const [lastUpdated, setLastUpdated] = useState(null)
    const [modalCancel, setModalCancel] = useState({
        open: false,
        campaign_id: null,
        campaign_name: "",
    })
    const [retryConfirmation, setRetryConfirmation] = useState(null)
    const [selectedIds, setSelectedIds] = useState([])
    const [selectAllMatching, setSelectAllMatching] = useState(false)
    const [bulkLoading, setBulkLoading] = useState(false)
    const [scheduleModal, setScheduleModal] = useState(false)
    const [comparisonModal, setComparisonModal] = useState(false)

    const { account_id } = useParams()
    const navigate = useNavigate()
    const { account, user } = useAuth()
    const queryKey = searchParams.toString()

    const headers = [
        <button type="button" className={styles.selectionButton} aria-label="Selecionar campanhas carregadas" onClick={() => toggleCurrentPageSelection()}><Checkbox checked={selectAllMatching || (campaigns.length > 0 && campaigns.every(item => selectedIds.includes(item._id)))} /></button>,
        "Campanha",
        "Status",
        "Agendamento",
        "Progresso",
        "Entrega",
        "Leitura",
        "Resposta",
        "Falhas",
        "",
    ]

    const fetchData = async (targetPage = 1, filterValues = appliedFilters, options = {}) => {
        try {
            if (options.append) setLoadingMore(true)
            else if (!options.silent) setLoading(true)
            if (options.silent) setRefreshing(true)

            const currentPage = Math.max(Number(targetPage) || 1, 1)
            const params = getParamsFromFilters(filterValues)
            params.set("page", currentPage)
            params.set("pageSize", 25)
            const response = await api.get(`/outbound-campaigns?${params.toString()}`)
            const data = response.data?.data || {}

            setCampaigns(previous => {
                if (options.append) {
                    return [...previous, ...(data.campaigns || []).filter(item => !previous.some(existing => existing._id === item._id))]
                }
                if (options.preserveLoaded && previous.length > (data.campaigns || []).length) {
                    const refreshedIds = new Set((data.campaigns || []).map(item => item._id))
                    return [...(data.campaigns || []), ...previous.filter(item => !refreshedIds.has(item._id))].slice(0, previous.length)
                }
                return data.campaigns || []
            })
            setCards(data.cards || [])
            setHighlights(data.highlights || [])
            setInboxOptions(data.filter_options?.inboxes || [])
            setMax(data.total || 0)
            if (!options.preserveLoaded) setPage(data.page || currentPage)
            setTotalPages(data.totalPages || 1)
            setLastUpdated(new Date())
        } catch (err) {
            toast.error(err?.response?.data?.message || "Não foi possível carregar as campanhas.")
        } finally {
            if (!options.silent) setLoading(false)
            setLoadingMore(false)
            setRefreshing(false)
        }
    }

    useEffect(() => {
        const nextFilters = getFiltersFromParams(searchParams)
        setFilters(nextFilters)
        setAppliedFilters(nextFilters)
        setSelectedIds([])
        setSelectAllMatching(false)
        fetchData(1, nextFilters)
    }, [queryKey])

    const updateFilter = (key, value) => {
        setFilters(previous => ({ ...previous, [key]: value }))
    }

    const handleApplyFilters = () => {
        const nextParams = getParamsFromFilters(filters)
        if (nextParams.toString() === queryKey) {
            setAppliedFilters(filters)
            fetchData(1, filters)
            return
        }
        setSearchParams(nextParams)
    }

    const handleClearFilters = () => {
        const defaults = getDefaultFilters()
        setFilters(defaults)
        setSearchParams(getParamsFromFilters(defaults))
    }

    const applyPreset = preset => {
        const defaults = getDefaultFilters()
        const nextFilters = preset === "today"
            ? { ...defaults, dateStart: moment().format("YYYY-MM-DD"), dateEnd: moment().format("YYYY-MM-DD") }
            : preset === "active"
                ? { ...defaults, status: ["scheduled", "sending", "paused"] }
                : preset === "failures"
                    ? { ...defaults, health: "with_failures" }
                    : defaults
        setFilters(nextFilters)
        setSearchParams(getParamsFromFilters(nextFilters))
    }

    const removeFilter = (key, value) => {
        const nextFilters = {
            ...appliedFilters,
            [key]: Array.isArray(appliedFilters[key]) ? appliedFilters[key].filter(item => item !== value) : "",
        }
        if (key === "period") {
            nextFilters.dateStart = ""
            nextFilters.dateEnd = ""
        }
        setFilters(nextFilters)
        setSearchParams(getParamsFromFilters(nextFilters))
    }

    const activeFilterChips = [
        ...((appliedFilters.dateStart || appliedFilters.dateEnd) ? [{ key: "period", id: "period", label: `Período: ${formatPeriod(appliedFilters.dateStart, appliedFilters.dateEnd)}` }] : []),
        ...(appliedFilters.status || []).map(id => ({ key: "status", id, label: getPropertyForItemInArrayById(id, optionsStatus, "id", "value") })),
        ...(appliedFilters.inboxes || []).map(id => ({ key: "inboxes", id, label: getPropertyForItemInArrayById(id, inboxOptions, "id", "value") })),
        ...(appliedFilters.search ? [{ key: "search", id: appliedFilters.search, label: `Busca: ${appliedFilters.search}` }] : []),
        ...(appliedFilters.health === "with_failures" ? [{ key: "health", id: "with_failures", label: "Com falhas" }] : []),
    ].filter(chip => chip.label)

    const toggleCampaignSelection = campaignId => {
        if (selectAllMatching) {
            setSelectAllMatching(false)
            setSelectedIds(campaigns.filter(item => item._id !== campaignId).map(item => item._id))
            return
        }
        setSelectedIds(previous => previous.includes(campaignId) ? previous.filter(id => id !== campaignId) : [...previous, campaignId])
    }

    const toggleCurrentPageSelection = () => {
        setSelectAllMatching(false)
        const currentIds = campaigns.map(item => item._id)
        const allSelected = currentIds.length > 0 && currentIds.every(id => selectedIds.includes(id))
        setSelectedIds(previous => allSelected
            ? previous.filter(id => !currentIds.includes(id))
            : [...new Set([...previous, ...currentIds])])
    }

    const selectionCount = selectAllMatching ? max : selectedIds.length
    const selectionPayload = {
        all_matching: selectAllMatching,
        ids: selectAllMatching ? [] : selectedIds,
        filters: {
            status: appliedFilters.status,
            inbox_id: appliedFilters.inboxes,
            dateStart: appliedFilters.dateStart,
            dateEnd: appliedFilters.dateEnd,
            search: appliedFilters.search,
            health: appliedFilters.health,
        },
    }

    const runBulkAction = async (action, extra = {}) => {
        if (!selectionCount) return
        setBulkLoading(true)
        try {
            const response = await api.post("/outbound-campaigns/bulk-action", { action, selection: selectionPayload, ...extra })
            const conflicts = response.data?.data?.conflicts || []
            toast.success(response.data?.message || "Campanhas atualizadas.")
            if (conflicts.length) toast.warning(`${conflicts.length} campanha${conflicts.length === 1 ? "" : "s"} próxima${conflicts.length === 1 ? "" : "s"} do novo horário. O agendamento foi mantido.`)
            setSelectedIds([])
            setSelectAllMatching(false)
            setScheduleModal(false)
            await fetchData(1, appliedFilters)
        } catch (err) {
            toast.error(err?.response?.data?.message || "Não foi possível atualizar as campanhas.")
            throw err
        } finally {
            setBulkLoading(false)
        }
    }

    const retryCampaignFailures = async (campaign, scheduled_at) => {
        setBulkLoading(true)
        try {
            const response = await api.post(`/outbound-campaigns/${campaign._id}/retry-failures`, { scheduled_at })
            toast.success(response.data?.message)
            setRetryConfirmation(null)
            await fetchData(1, appliedFilters)
        } catch (err) {
            if (err?.response?.data?.code === "campaign_failure_metrics_reconciled") {
                toast.warning(err.response.data.message)
                setRetryConfirmation(null)
                await fetchData(1, appliedFilters)
            } else {
                toast.error(err?.response?.data?.message || "Não foi possível reenviar as falhas.")
            }
        } finally {
            setBulkLoading(false)
        }
    }

    const runCampaignAction = async (campaign, action) => {
        setBulkLoading(true)
        try {
            const response = await api.post("/outbound-campaigns/bulk-action", { action, selection: { ids: [campaign._id] } })
            toast.success(response.data?.message || "Campanha atualizada.")
            await fetchData(1, appliedFilters)
        } catch (err) {
            toast.error(err?.response?.data?.message || "Não foi possível atualizar a campanha.")
        } finally {
            setBulkLoading(false)
        }
    }

    const handleUseAsBase = async campaign => {
        try {
            setLoading(true)
            const response = await api.get(`/outbound-campaigns/${campaign._id}?page=1&pageSize=1`)
            navigate(`/${account_id}/integrations/chat/outbound-campaign`, {
                state: {
                    campaignBase: {
                        name: `${campaign.name} - cópia`,
                        description: campaign.description || "",
                        inbox_id: campaign.inbox?._id,
                        update_conversations: response.data?.campaign?.update_conversations !== false,
                        template_name: response.data?.message?.template_name,
                        template_language: response.data?.message?.template_language,
                    },
                },
            })
        } catch (err) {
            toast.error(err?.response?.data?.message || "Não foi possível preparar uma campanha semelhante.")
        } finally {
            setLoading(false)
        }
    }

    const getCampaignRows = list => list.map(campaign => {
        const actions = [{
            label: "Detalhes",
            onClick: id => navigate(`/${account_id}/integrations/chat/outbound-campaign/${id}`),
        }, {
            label: "Usar como base",
            onClick: () => handleUseAsBase(campaign),
        }]
        if (["scheduled", "sending"].includes(campaign.status)) actions.push({ label: "Pausar", onClick: () => runCampaignAction(campaign, "pause") })
        if (campaign.status === "paused") actions.push({ label: "Retomar", onClick: () => runCampaignAction(campaign, "resume") })
        if (campaign.failed > 0 || Number(campaign.retry_summary?.failed || 0) > 0) actions.push({
            label: "Reenviar mensagens com falha",
            onClick: () => {
                if (campaign.status !== "finished") {
                    toast.info("O reenvio ficará disponível assim que a campanha for finalizada.")
                    return
                }
                const eligible = Number(campaign.retry_eligible ?? campaign.failed ?? 0)
                const retryState = campaign.retry_summary?.state || (eligible > 0 ? "available" : "limit_reached")
                if (retryState === "metrics_mismatch") {
                    retryCampaignFailures(campaign)
                    return
                }
                if (retryState === "limit_reached") {
                    toast.info("Todas as mensagens com falha já atingiram o limite de três novas tentativas.")
                    return
                }
                if (retryState !== "available") {
                    toast.info("Não existem mensagens com falha para reenviar.")
                    return
                }
                setRetryConfirmation({ ...campaign, retry_eligible: eligible })
            },
        })
        if (campaign.status !== "finished" && campaign.status !== "canceled") {
            actions.push({
                label: "Cancelar o envio",
                onClick: () => openCancelModal(campaign),
            })
        }

        const total = Number(campaign.total || 0)
        const processed = Number(campaign.sent || 0) + Number(campaign.failed || 0) + Number(campaign.canceled || 0)
        const progress = total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0

        return {
            id: campaign._id,
            value: [
                <button type="button" className={styles.selectionButton} aria-label={`Selecionar ${campaign.name}`} onClick={event => { event.stopPropagation(); toggleCampaignSelection(campaign._id) }}><Checkbox checked={selectAllMatching || selectedIds.includes(campaign._id)} /></button>,
                <div className={styles.campaignCell}>
                    <Link to={`/${account_id}/integrations/chat/outbound-campaign/${campaign._id}`}>{campaign.name}</Link>
                    <span><MdInbox /> {campaign.inbox?.name || "Caixa removida"} · {total} destinatário{total === 1 ? "" : "s"}</span>
                    <span><MdPersonOutline /> {campaign.creator?.name || "Criador não identificado"}{campaign.created_at ? ` · ${moment(campaign.created_at).format("DD/MM/YYYY")}` : ""}</span>
                </div>,
                <span className={`${styles.statusBadge} ${styles[`statusBadge_${statusTone[campaign.status] || "neutral"}`]}`}>
                    {getPropertyForItemInArrayById(campaign.status, optionsStatus, "id", "value")}
                </span>,
                <div className={styles.scheduleCell}>
                    <strong>{campaign.scheduled_at ? moment(campaign.scheduled_at).format("DD/MM/YYYY") : "Sem data"}</strong>
                    <span>{campaign.scheduled_at ? moment(campaign.scheduled_at).format("HH:mm") : "-"}</span>
                </div>,
                <div className={styles.progressCell}>
                    <div><span style={{ width: `${progress}%` }} /></div>
                    <small>{progress}% · {processed}/{total}</small>
                </div>,
                <span className={styles.rateCell}>{campaign.delivery_rate || 0}%<small>{campaign.delivered || 0} entregues</small></span>,
                <span className={styles.rateCell}>{campaign.read_rate || 0}%<small>{campaign.read || 0} lidas</small></span>,
                <span className={styles.rateCell}>{campaign.replied_rate || 0}%<small>{campaign.replied || 0} respostas</small></span>,
                <span className={`${styles.failureCell} ${campaign.failed > 0 ? styles.hasFailures : ""}`}>{campaign.failed || 0}</span>,
            ],
            actions,
        }
    })

    const getAllFilteredCampaigns = async () => {
        const pageSize = 500
        let currentPage = 1
        let total = 0
        let results = []
        let reportCards = cards

        do {
            const params = getParamsFromFilters(appliedFilters)
            params.set("page", currentPage)
            params.set("pageSize", pageSize)
            const response = await api.get(`/outbound-campaigns?${params.toString()}`)
            const data = response.data?.data || {}
            if (currentPage === 1) reportCards = data.cards || cards
            total = data.total || 0
            results = [...results, ...(data.campaigns || [])]
            currentPage += 1
        } while (results.length < total)

        return { campaigns: results, cards: reportCards }
    }

    const buildOverviewPdf = async (selectedOnly = false) => {
        const report = await getAllFilteredCampaigns()
        const reportCampaigns = selectedOnly && !selectAllMatching
            ? report.campaigns.filter(campaign => selectedIds.includes(campaign._id))
            : report.campaigns
        return {
            title: selectedOnly ? "Campanhas selecionadas" : "Histórico de Campanhas",
            accountName: account?.company?.fantasy_name || account?.name || "Conta",
            generatedBy: user?.name,
            filters: [
                { label: "Período", value: formatPeriod(appliedFilters.dateStart, appliedFilters.dateEnd) },
                { label: "Status", value: getOptionLabels(appliedFilters.status, optionsStatus) },
                { label: "Caixas de entrada", value: getOptionLabels(appliedFilters.inboxes, inboxOptions) },
                appliedFilters.search ? { label: "Busca", value: appliedFilters.search } : null,
                appliedFilters.health === "with_failures" ? { label: "Saúde", value: "Campanhas com falhas" } : null,
                { label: "Ordenação", value: getPropertyForItemInArrayById(appliedFilters.sort, sortOptions, "id", "value") || "Mais recentes" },
            ],
            data: {
                cards: report.cards,
                tables: [{
                    title: "Campanhas",
                    headers: ["Campanha", "Criada por", "Caixa", "Status", "Agendamento", "Total", "Enviadas", "Entregues", "Lidas", "Respostas", "Falhas", "Entrega", "Leitura", "Resposta"],
                    values: reportCampaigns.map(campaign => ({
                        value: [
                            campaign.name,
                            campaign.creator?.name || "Criador não identificado",
                            campaign.inbox?.name || "Caixa removida",
                            getPropertyForItemInArrayById(campaign.status, optionsStatus, "id", "value"),
                            moment(campaign.scheduled_at).format("DD/MM/YYYY HH:mm"),
                            campaign.total || 0,
                            campaign.sent || 0,
                            campaign.delivered || 0,
                            campaign.read || 0,
                            campaign.replied || 0,
                            campaign.failed || 0,
                            `${campaign.delivery_rate || 0}%`,
                            `${campaign.read_rate || 0}%`,
                            `${campaign.replied_rate || 0}%`,
                        ],
                    })),
                }],
            },
        }
    }

    const openCancelModal = campaign => {
        setModalCancel({
            open: true,
            bulk: false,
            campaign_id: campaign._id,
            campaign_name: campaign.name,
        })
    }

    const closeCancelModal = () => {
        setModalCancel({ open: false, bulk: false, campaign_id: null, campaign_name: "" })
    }

    const handleCancelCampaign = async () => {
        if (modalCancel.bulk) {
            try {
                await runBulkAction("cancel")
                closeCancelModal()
            } catch (_err) {
                // A notificação é exibida pelo executor da ação.
            }
            return
        }
        setLoading(true)
        try {
            await api.post(`/outbound-campaigns/${modalCancel.campaign_id}/cancel`)
            toast.success("Campanha cancelada com sucesso!")
            closeCancelModal()
            fetchData(1, appliedFilters)
        } catch (err) {
            toast.error(err?.response?.data?.message || "Erro ao cancelar campanha")
        } finally {
            setLoading(false)
        }
    }

    const cardsById = Object.fromEntries(cards.map(card => [card.id, card]))
    const handleProgressiveLoad = useCallback(() => {
        if (!loading && !loadingMore && !refreshing && page < totalPages) {
            fetchData(page + 1, appliedFilters, { append: true })
        }
    }, [loading, loadingMore, refreshing, page, totalPages, appliedFilters])
    const summaryMetrics = [
        "total_campaigns",
        "sending_campaigns",
        "scheduled_campaigns",
        "messages_sent",
        "delivery_rate",
        "reply_rate",
        "messages_failed",
    ].map(id => cardsById[id]).filter(Boolean)
    const periodLabel = formatPeriod(appliedFilters.dateStart, appliedFilters.dateEnd)

    return (
        <div className={styles.container}>
            <div className={styles.topBar}>
                <BackButton fallback={`/${account_id}/integrations/chat`} />
                <div className={styles.reportActions}>
                    {lastUpdated && (
                        <span className={styles.updatedAt}>
                            <i className={refreshing ? styles.refreshingDot : ""} />
                            Atualizado às {moment(lastUpdated).format("HH:mm:ss")}
                        </span>
                    )}
                    <Button variant="secondary" fullWidth={false} onClick={() => fetchData(1, appliedFilters, { silent: true, preserveLoaded: true })} disabled={refreshing}>
                        <MdOutlineRefresh className={refreshing ? styles.spinning : ""} /> {refreshing ? "Atualizando" : "Atualizar"}
                    </Button>
                    <PdfExportButton
                        buildReport={buildOverviewPdf}
                        disabled={loading}
                        fullWidth={false}
                    />
                    <Button fullWidth={false} onClick={() => navigate(`/${account_id}/integrations/chat/outbound-campaign`)}>
                        <MdAdd /> Nova campanha
                    </Button>
                </div>
            </div>

            <section className={styles.hero}>
                <div>
                    <span className={styles.eyebrow}>Visão operacional</span>
                    <h1>Campanhas</h1>
                    <p>Acompanhe agendamentos, resultados e pontos de atenção em um só lugar.</p>
                </div>
                <div className={styles.heroPeriod}>
                    <MdAccessTime />
                    <div><span>Período analisado</span><strong>{periodLabel}</strong></div>
                </div>
            </section>

            {summaryMetrics.length > 0 && (
                <section className={styles.summaryGrid}>
                    {summaryMetrics.map(metric => {
                        const Icon = summaryIcons[metric.id] || MdOutlineCampaign
                        return (
                            <div key={metric.id} className={`${styles.summaryCard} ${metric.id === "messages_failed" ? styles.summaryDanger : ""}`}>
                                <span className={styles.summaryIcon}><Icon /></span>
                                <div><span>{metric.name}</span><strong>{metric.value}</strong></div>
                            </div>
                        )
                    })}
                </section>
            )}

            {highlights.length > 0 && (
                <section className={styles.alertsSection}>
                    <div className={styles.sectionHeading}>
                        <div><span className={styles.eyebrow}>Análise</span><h2>Destaques do período</h2></div>
                        {Number(cardsById.messages_failed?.value || 0) > 0 && (
                            <Button variant="secondary" size="sm" fullWidth={false} onClick={() => applyPreset("failures")}>
                                Ver campanhas com falhas
                            </Button>
                        )}
                    </div>
                    <div className={styles.alertsGrid}>
                        {highlights.map(item => (
                            <div key={item.id} className={`${styles.alertItem} ${styles[`alertItem_${item.type}`]}`}>
                                {item.type === "danger" || item.type === "warning" ? <MdErrorOutline /> : <MdDoneAll />}
                                <div><strong>{item.title}</strong><span>{item.description}</span></div>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            <section className={styles.campaignsSection}>
                <div className={styles.sectionHeading}>
                    <div><span className={styles.eyebrow}>Histórico</span><h2>Lista de campanhas</h2><p>{max} campanha{max === 1 ? "" : "s"} encontrada{max === 1 ? "" : "s"}</p></div>
                    {loading && lastUpdated && <span className={styles.loadingResults}>Atualizando resultados…</span>}
                </div>

                <div className={styles.presets}>
                    <Button variant="secondary" size="sm" fullWidth={false} onClick={() => applyPreset("today")}>Hoje</Button>
                    <Button variant="secondary" size="sm" fullWidth={false} onClick={() => applyPreset("active")}>Em andamento</Button>
                    <Button variant="secondary" size="sm" fullWidth={false} onClick={() => applyPreset("failures")}>Com falhas</Button>
                    <Button variant="secondary" size="sm" fullWidth={false} onClick={() => applyPreset("30days")}>Últimos 30 dias</Button>
                </div>

                <div className={styles.filters}>
                    <DatePicker
                        name="Período"
                        multiple
                        dateStart={filters.dateStart}
                        dateEnd={filters.dateEnd}
                        onChange={(start, end) => {
                            updateFilter("dateStart", start)
                            updateFilter("dateEnd", end)
                        }}
                    />
                    <Select name="Caixas de entrada" placeholder="Pesquise uma caixa" multiple options={inboxOptions} selected={filters.inboxes} onChange={value => updateFilter("inboxes", value)} />
                    <Select name="Status" placeholder="Pesquise um status" multiple options={optionsStatus} selected={filters.status} onChange={value => updateFilter("status", value)} />
                    <Select name="Ordenar por" placeholder="Pesquise uma ordenação" options={sortOptions} selected={filters.sort} onChange={value => updateFilter("sort", value || "recent")} allowDeselectAll={false} />
                    <Input type="search" placeholder="Buscar campanha..." value={filters.search} onChange={event => updateFilter("search", event.target.value)} onKeyDown={event => event.key === "Enter" && handleApplyFilters()} />
                    <div className={styles.filterActions}>
                        <Button variant="primary" onClick={handleApplyFilters}>Aplicar filtros</Button>
                        <Button variant="secondary" onClick={handleClearFilters}><MdFilterAltOff /> Limpar</Button>
                    </div>
                </div>

                {activeFilterChips.length > 0 && (
                    <div className={styles.filterChips}>
                        <span>Filtros ativos</span>
                        {activeFilterChips.map(chip => (
                            <button key={`${chip.key}-${chip.id}`} type="button" onClick={() => removeFilter(chip.key, chip.id)} title="Remover filtro">
                                {chip.label} <b>×</b>
                            </button>
                        ))}
                    </div>
                )}

                {selectionCount > 0 && (
                    <div className={styles.bulkBar}>
                        <div>
                            <strong>{selectionCount} campanha{selectionCount === 1 ? "" : "s"} selecionada{selectionCount === 1 ? "" : "s"}</strong>
                            {!selectAllMatching && selectedIds.length === campaigns.length && max > campaigns.length && (
                                <button type="button" onClick={() => setSelectAllMatching(true)}>Selecionar todas as {max} campanhas filtradas</button>
                            )}
                            {selectAllMatching && <button type="button" onClick={() => { setSelectAllMatching(false); setSelectedIds([]) }}>Limpar seleção total</button>}
                        </div>
                        <div className={styles.bulkActions}>
                            <Button size="sm" variant="secondary" fullWidth={false} onClick={() => runBulkAction("pause")} disabled={bulkLoading}><MdPause /> Pausar</Button>
                            <Button size="sm" variant="secondary" fullWidth={false} onClick={() => runBulkAction("resume")} disabled={bulkLoading}><MdPlayArrow /> Retomar</Button>
                            <Button size="sm" variant="secondary" fullWidth={false} onClick={() => setScheduleModal(true)} disabled={bulkLoading}><MdSchedule /> Reagendar</Button>
                            <Button size="sm" variant="secondary" fullWidth={false} onClick={() => runBulkAction("duplicate")} disabled={bulkLoading || selectionCount > 20}><MdContentCopy /> Duplicar</Button>
                            <Button size="sm" variant="secondary" fullWidth={false} onClick={() => setComparisonModal(true)} disabled={selectAllMatching || selectedIds.length < 2 || selectedIds.length > 5}><MdCompareArrows /> Comparar</Button>
                            <PdfExportButton buildReport={() => buildOverviewPdf(true)} disabled={bulkLoading} fullWidth={false} size="sm" label="PDF" />
                            <Button size="sm" variant="danger" fullWidth={false} onClick={() => setModalCancel({ open: true, bulk: true, campaign_name: `${selectionCount} campanhas` })} disabled={bulkLoading}>Cancelar</Button>
                        </div>
                    </div>
                )}

                <div className={styles.tables}>
                    <Table
                        headers={headers}
                        values={getCampaignRows(campaigns)}
                        onScroll={handleProgressiveLoad}
                        loadMore={loadingMore}
                        messageNotRegisters="Nenhuma campanha encontrada para os filtros selecionados."
                    />
                </div>
                <div className={styles.progressiveStatus}>
                    <span>{campaigns.length} de {max} campanhas carregadas</span>
                    {campaigns.length < max && !loadingMore && <small>Role a lista para carregar mais</small>}
                </div>
            </section>
            {modalCancel.open && (
                <ModalConfirm
                    title="Cancelar envio de campanha"
                    description="Essa ação não pode ser desfeita"
                    text={modalCancel.bulk ? `Tem certeza que deseja cancelar ${selectionCount} campanhas selecionadas? Campanhas finalizadas serão ignoradas.` : `Tem certeza que deseja cancelar o envio da campanha "${modalCancel.campaign_name}"?`}
                    onSubmit={handleCancelCampaign}
                    closeModal={closeCancelModal}
                />
            )}
            {retryConfirmation && (
                <CampaignRetryModal
                    eligible={retryConfirmation.retry_eligible}
                    onSubmit={scheduled_at => retryCampaignFailures(retryConfirmation, scheduled_at)}
                    closeModal={() => setRetryConfirmation(null)}
                />
            )}
            {scheduleModal && <CampaignScheduleModal count={selectionCount} onSubmit={scheduled_at => runBulkAction("reschedule", { scheduled_at })} closeModal={() => setScheduleModal(false)} />}
            {comparisonModal && <CampaignComparisonModal campaignIds={selectedIds} closeModal={() => setComparisonModal(false)} />}
            {loading && !lastUpdated && <Loading fullPage />}
        </div>
    )
}

export default OutboundCampaignList
